/**
 * What the storefront knows about the location the diner is browsing
 * (ORDR-118; FR-STO-023, 024, 025, FR-QTE-019).
 *
 * MODE KNOWN: #2 says whether the location takes orders, from the LAST-ORDER
 * CUTOFF, and is re-read ONCE at the next boundary it names (the earliest of
 * `last_order_at`, `closes_at`, `next_opens_at` still in the future, plus a
 * second) and whenever the page becomes visible. A diner building a cart at
 * 01:29 is told at 01:30, not after choosing.
 *
 * MODE NOT KNOWN (the flow's MODE step is still REQUIRED): nothing
 * mode-specific is read. The published hours and the readiness estimate are
 * shown, no open state, and the server still refuses at placement.
 *
 * Every answer is keyed by what it answers, so a late reply for a previous
 * location or mode is never shown as current.
 */

import { useEffect, useState } from "react";
import type { ApiClient } from "../api/client.js";
import {
  readHours,
  readReadiness,
  readSchedule,
  readWindows,
  type Hours,
  type Readiness,
  type Schedule,
  type StorefrontRead,
  type Windows,
} from "./storefrontApi.js";

const READINESS_INTERVAL_MS = 60_000;
/** setTimeout's ceiling; a longer boundary is re-armed by the next answer. */
const MAX_DELAY_MS = 2_147_483_647;

export interface UseStorefrontOptions {
  readonly client: ApiClient;
  readonly locationNodeId: string | null;
  /** The flow's `fulfilment_mode`, verbatim; `null` while MODE is REQUIRED. */
  readonly mode: string | null;
}

export interface Storefront {
  /** `null` while loading, and always when the mode is not yet known. */
  readonly hours: StorefrontRead<"hours", Hours> | null;
  readonly schedule: StorefrontRead<"schedule", Schedule> | null;
  /** Read only for a WINDOWED mode. */
  readonly windows: StorefrontRead<"windows", Windows> | null;
  /** Only while the location may take orders; `null` otherwise. */
  readonly readiness: StorefrontRead<"readiness", Readiness> | null;
  /** Words refusing placement, from the latest #2 answer; `null` when it may proceed. */
  readonly placementRefusal: string | null;
}

interface Keyed<T> {
  readonly key: string;
  readonly value: T;
}

/** Reads `load()` whenever `key` or `tick` changes; the answer is kept against its key. */
function useKeyedRead<T>(key: string | null, tick: number, load: () => Promise<T>): T | null {
  const [answer, setAnswer] = useState<Keyed<T> | null>(null);
  useEffect(() => {
    if (key === null) {
      return;
    }
    let live = true;
    void load().then((value) => {
      if (live) {
        setAnswer({ key, value });
      }
    });
    return () => {
      live = false;
    };
    // `load` closes over exactly what `key` names; the key is the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);
  return key !== null && answer !== null && answer.key === key ? answer.value : null;
}

function nextBoundaryMs(hours: Hours, now: number): number | null {
  const future = [hours.last_order_at, hours.closes_at, hours.next_opens_at]
    .filter((instant): instant is string => instant !== null)
    .map((instant) => Date.parse(instant))
    .filter((ms) => Number.isFinite(ms) && ms > now);
  return future.length === 0 ? null : Math.min(...future);
}

function isVisible(): boolean {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

export function refusalFor(hours: Hours): string | null {
  return hours.accepts_orders
    ? null
    : "This location is not taking orders right now, so this order cannot be placed. Nothing has been charged.";
}

export function useStorefront({ client, locationNodeId, mode }: UseStorefrontOptions): Storefront {
  const [hoursTick, setHoursTick] = useState(0);
  const [readinessTick, setReadinessTick] = useState(0);

  const hoursKey = locationNodeId === null || mode === null ? null : `${locationNodeId}|${mode}`;
  const hours = useKeyedRead(hoursKey, hoursTick, () => readHours(client, locationNodeId ?? "", mode ?? ""));
  const schedule = useKeyedRead(locationNodeId, 0, () => readSchedule(client, locationNodeId ?? ""));

  const openHours = hours?.kind === "ok" ? hours.hours : null;
  const windowed = openHours !== null && openHours.temporal_shape === "WINDOWED";
  const windows = useKeyedRead(windowed ? hoursKey : null, 0, () => readWindows(client, locationNodeId ?? "", mode ?? ""));

  // Readiness is an aid for a location that may take orders. With the mode
  // known it waits for #2 to say so; closed, it is never asked.
  const readinessWanted = locationNodeId !== null && (mode === null || openHours?.accepts_orders === true);
  const readinessKey = readinessWanted ? `${locationNodeId}|${mode ?? ""}` : null;
  const readiness = useKeyedRead(readinessKey, readinessTick, () =>
    readReadiness(client, locationNodeId ?? "", mode),
  );

  // ONE re-read at the next boundary. A new answer replaces the timer.
  useEffect(() => {
    if (openHours === null) {
      return;
    }
    const now = Date.now();
    const boundary = nextBoundaryMs(openHours, now);
    if (boundary === null) {
      return;
    }
    const timer = setTimeout(
      () => setHoursTick((tick) => tick + 1),
      Math.min(boundary - now + 1_000, MAX_DELAY_MS),
    );
    return () => clearTimeout(timer);
  }, [openHours]);

  useEffect(() => {
    function onVisibility(): void {
      if (isVisible()) {
        setHoursTick((tick) => tick + 1);
      }
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (readinessKey === null) {
      return;
    }
    const timer = setInterval(() => {
      if (isVisible()) {
        setReadinessTick((tick) => tick + 1);
      }
    }, READINESS_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [readinessKey]);

  return {
    hours,
    schedule,
    windows: windowed ? windows : null,
    readiness: readinessWanted ? readiness : null,
    placementRefusal: openHours === null ? null : refusalFor(openHours),
  };
}
